import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  UserCircleIcon,
  EnvelopeIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import api from "../helpers/axios";
import empty_pfp from "../assets/empty_pfp.jpg";
import DashboardMetricsService from "../services/dashboardMetricsService";
import { useDashboardUI } from "../context/GlobalContext";
import { buildAnalyticsQueryParams, formatRole } from "../helpers/helpers";
import { AnalyticsChartCard } from "../components/ChartComponents";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

const Card = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
    {title && (
      <h3 className="text-sm font-semibold mb-3 text-gray-800">{title}</h3>
    )}
    {children}
  </div>
);

const Accordion = ({ title, children, isOpen = true }) => {
  const [open, setOpen] = useState(isOpen);

  return (
    <div className="border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-3 bg-gray-50 hover:bg-gray-100"
      >
        <span className="text-md font-semibold">{title}</span>
        <span>
          {open ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </span>
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
};

// Helper functions from FileAnalytics
const FILE_TYPE_CATEGORIES = [
  "Images",
  "PDFs",
  "CSV_Excel",
  "Text",
  "HTML",
  "Others",
];

const FILE_SIZE_BUCKETS = [
  "1_10_MB",
  "10_20_MB",
  "20_30_MB",
  "30_40_MB",
  "40_50_MB",
  "50_plus_MB",
];

const SIZE_BUCKET_LABELS = {
  "1_10_MB": "1–10 MB",
  "10_20_MB": "10–20 MB",
  "20_30_MB": "20–30 MB",
  "30_40_MB": "30–40 MB",
  "40_50_MB": "40–50 MB",
  "50_plus_MB": "50+ MB",
};

const PIE_COLORS = [
  "#6366F1",
  "#095023ff",
  "#F59E0B",
  "#EF4444",
  "#44cce4ff",
  "#b070ecff",
];

const COLORS = {
  secondary: "#6366F1",
  primary: "#22C55E",
};

const MODEL_COLORS = [
  "#6366F1",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#0EA5E9",
  "#8B5CF6",
];

function mergeAnalyticsByDate(metrics) {
  const map = {};

  metrics.forEach(({ key, source, valueKey }) => {
    (source || []).forEach((item) => {
      if (!map[item.date]) {
        map[item.date] = { date: item.date };
      }
      map[item.date][key] = item[valueKey] || 0;
    });
  });

  return Object.values(map).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
}

function transformFilesByType(filesByType = {}) {
  return FILE_TYPE_CATEGORIES.map((category) => ({
    name: category.replace("_", "/"),
    count: filesByType?.[category] ?? 0,
  }));
}

function transformSizeBucketToPieData(sizeBuckets = {}) {
  return FILE_SIZE_BUCKETS.map((bucket, index) => ({
    name: SIZE_BUCKET_LABELS[bucket],
    value: sizeBuckets?.[bucket] ?? 0,
    color: PIE_COLORS[index % PIE_COLORS.length],
  }));
}

function transformModelUsageToPieData(models = []) {
  return models.map((item, index) => ({
    name: item.model,
    value: item.count || 0,
    color: MODEL_COLORS[index % MODEL_COLORS.length],
  }));
}

const CustomDropdown = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-40 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
      >
        {selectedOption.label}
        <ChevronDownIcon
          className={`w-4 h-4 ml-2 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 origin-top-right rounded-lg bg-white shadow-lg border border-slate-100 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden">
          <div className="py-1">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center px-4 py-2 text-sm transition-colors ${value === option.value
                  ? "bg-slate-50 text-blue-600 font-semibold"
                  : "text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const UserDetail = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!user);

  const {
    selectedFacilityFilter,
  } = useDashboardUI();

  // Date range state for analytics
  const [dateRange, setDateRange] = useState("7"); // "7", "15", "30"

  // Analytics states
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [projectAnalytics, setProjectAnalytics] = useState(null);
  const [fileAnalytics, setFileAnalytics] = useState(null);
  const [fileGenerationAnalytics, setFileGenerationAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      if (user) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser(data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId, user]);

  // Fetch analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setAnalyticsLoading(true);

        // Calculate startValue and endValue based on dateRange
        const endValue = DateTime.local().endOf("day").toJSDate();
        const startValue = DateTime.local()
          .minus({ days: parseInt(dateRange, 10) })
          .startOf("day")
          .toJSDate();

        const queryParams = buildAnalyticsQueryParams({
          startValue,
          endValue,
          facilityId: selectedFacilityFilter?.facility_number,
          extra: { user_id: userId },
        });

        const [platformRes, projectRes, collectionRes, fileRes, fileGenRes] =
          await Promise.allSettled([
            DashboardMetricsService.getPlatformUsage(queryParams),
            DashboardMetricsService.getProjectAnalytics(queryParams),
            DashboardMetricsService.getCollectionAnalytics(queryParams),
            DashboardMetricsService.getFileUploadAnalytics(queryParams),
            DashboardMetricsService.getFileGenerationAnalytics(queryParams),
          ]);

        setPlatformAnalytics(
          platformRes.status === "fulfilled" ? platformRes.value.data : null
        );

        setProjectAnalytics({
          project:
            projectRes.status === "fulfilled" ? projectRes.value.data : null,
          collection:
            collectionRes.status === "fulfilled"
              ? collectionRes.value.data
              : null,
        });

        setFileAnalytics(
          fileRes.status === "fulfilled" ? { files: fileRes.value.data } : null
        );

        setFileGenerationAnalytics(
          fileGenRes.status === "fulfilled" ? fileGenRes.value.data : null
        );
      } catch (error) {
        console.error("Error fetching analytics:", error);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    if (user) {
      fetchAnalytics();
    }
  }, [user, userId, dateRange, selectedFacilityFilter]);

  // Platform Usage Charts
  const searchUsageChart = useMemo(() => {
    if (!platformAnalytics) return { chartData: [], lines: [] };

    const METRIC_CONFIG = {
      web_search: {
        metricKey: "web_search_usage",
        chartKey: "web_search",
        valueKey: "total_count",
        label: "Web Search",
        color: "#6366F1",
      },
      intranet_search: {
        metricKey: "intranet_search_usage",
        chartKey: "intranet_search",
        valueKey: "total_count",
        label: "Intranet Search",
        color: "#22C55E",
      },
    };

    const metrics = Object.values(METRIC_CONFIG).map((config) => ({
      mergeConfig: {
        key: config.chartKey,
        source: platformAnalytics?.[config.metricKey] || [],
        valueKey: config.valueKey,
      },
      lineConfig: {
        dataKey: config.chartKey,
        stroke: config.color,
        name: config.label,
      },
    }));

    return {
      chartData: mergeAnalyticsByDate(metrics.map((m) => m.mergeConfig)),
      lines: metrics.map((m) => m.lineConfig),
    };
  }, [platformAnalytics]);

  const modelUsagePieData = useMemo(() => {
    if (!platformAnalytics) return [];
    return transformModelUsageToPieData(platformAnalytics.models_usage);
  }, [platformAnalytics]);

  const totalModelUsage = modelUsagePieData.reduce(
    (sum, d) => sum + d.value,
    0
  );

  // Project Analytics Charts
  const METRIC_CONFIG = {
    projects_created: {
      source: "project",
      metricKey: "projects_created",
      chartKey: "projects",
      valueKey: "total_projects",
      label: "Projects",
      color: "#6366F1",
    },
    collections_created: {
      source: "collection",
      metricKey: "collections_created",
      chartKey: "collections",
      valueKey: "total_projects",
      label: "Collections",
      color: "#22C55E",
    },
    project_files_uploaded: {
      source: "project",
      metricKey: "files_uploaded",
      chartKey: "project_files",
      valueKey: "total_files",
      label: "Project Files",
      color: "#6366F1",
    },
    collection_files_uploaded: {
      source: "collection",
      metricKey: "files_uploaded",
      chartKey: "collection_files",
      valueKey: "total_files",
      label: "Collection Files",
      color: "#22C55E",
    },
    project_chats_created: {
      source: "project",
      metricKey: "chats_created",
      chartKey: "project_chats",
      valueKey: "total_chats",
      label: "Project Chats",
      color: "#6366F1",
    },
    collection_chats_created: {
      source: "collection",
      metricKey: "chats_created",
      chartKey: "collection_chats",
      valueKey: "total_chats",
      label: "Collection Chats",
      color: "#22C55E",
    },
  };

  const buildChartData = (metricKeys) => {
    const activeMetrics = metricKeys.map((key) => {
      const config = METRIC_CONFIG[key];
      const sourceData =
        projectAnalytics?.[config.source]?.[config.metricKey] || [];

      return {
        mergeConfig: {
          key: config.chartKey,
          source: sourceData,
          valueKey: config.valueKey,
        },
        lineConfig: {
          dataKey: config.chartKey,
          stroke: config.color,
          name: config.label,
        },
      };
    });

    return {
      chartData: mergeAnalyticsByDate(
        activeMetrics.map((m) => m.mergeConfig)
      ),
      lines: activeMetrics.map((m) => m.lineConfig),
    };
  };

  const projectChart = useMemo(
    () =>
      projectAnalytics
        ? buildChartData(["projects_created", "collections_created"])
        : { chartData: [], lines: [] },
    [projectAnalytics]
  );

  const projectFileChart = useMemo(
    () =>
      projectAnalytics
        ? buildChartData([
          "project_files_uploaded",
          "collection_files_uploaded",
        ])
        : { chartData: [], lines: [] },
    [projectAnalytics]
  );

  const projectChatChart = useMemo(
    () =>
      projectAnalytics
        ? buildChartData([
          "project_chats_created",
          "collection_chats_created",
        ])
        : { chartData: [], lines: [] },
    [projectAnalytics]
  );

  // File Analytics Charts
  const FILE_METRIC_CONFIG = {
    files_uploaded_daywise: {
      source: "files",
      metricKey: "files_uploaded_daywise",
      chartKey: "files",
      valueKey: "total_files",
      label: "Files Uploaded",
      color: "#22C55E",
    },
  };

  const fileChart = useMemo(() => {
    if (!fileAnalytics) return { chartData: [], lines: [] };

    const config = FILE_METRIC_CONFIG.files_uploaded_daywise;
    const sourceData = fileAnalytics?.[config.source]?.[config.metricKey] || [];

    return {
      chartData: mergeAnalyticsByDate([
        {
          key: config.chartKey,
          source: sourceData,
          valueKey: config.valueKey,
        },
      ]),
      lines: [
        {
          dataKey: config.chartKey,
          stroke: config.color,
          name: config.label,
        },
      ],
    };
  }, [fileAnalytics]);

  const filesByTypeData = useMemo(() => {
    if (!fileAnalytics) return [];
    return transformFilesByType(fileAnalytics?.files?.files_by_type);
  }, [fileAnalytics]);

  const sizeBucketPieData = useMemo(() => {
    if (!fileAnalytics) return [];
    return transformSizeBucketToPieData(
      fileAnalytics?.files?.files_by_size_bucket
    );
  }, [fileAnalytics]);

  const sizeBucketTotal = useMemo(() => {
    return sizeBucketPieData.reduce((sum, d) => sum + d.value, 0);
  }, [sizeBucketPieData]);

  // File Generation Analytics Charts
  const fileGenChart = useMemo(() => {
    const sourceData = fileGenerationAnalytics?.files_generated_datewise || [];

    return {
      chartData: mergeAnalyticsByDate([
        {
          key: "total_files_generated",
          source: sourceData,
          valueKey: "total_files_generated",
        },
      ]),
      lines: [
        {
          dataKey: "total_files_generated",
          stroke: "#6366F1",
          name: "Files Generated",
        },
      ],
    };
  }, [fileGenerationAnalytics]);

  const imageGenChart = useMemo(() => {
    const sourceData = fileGenerationAnalytics?.images_generated_datewise || [];

    return {
      chartData: mergeAnalyticsByDate([
        {
          key: "total_images_generated",
          source: sourceData,
          valueKey: "total_images_generated",
        },
      ]),
      lines: [
        {
          dataKey: "total_images_generated",
          stroke: "#22C55E",
          name: "Images Generated",
        },
      ],
    };
  }, [fileGenerationAnalytics]);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2B3674]"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">User not found</p>
          <button
            onClick={() => navigate("/user-management")}
            className="mt-4 text-blue-500 hover:underline"
          >
            Back to Users
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 p-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/user-management")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">User Details</h1>
      </div>

      {/* User Information Card */}
      <Card>
        <div className="flex items-start gap-6">
          <img
            src={empty_pfp}
            alt="Profile"
            className="h-24 w-24 rounded-full border-2 border-gray-200"
          />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <UserCircleIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-800">
                  {user.display_name}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <EnvelopeIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-800">
                  {user.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <UserCircleIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Role</p>
                <p className="text-sm font-medium text-gray-800 capitalize">
                  {formatRole(user.user_role)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {user.is_active ? (
                <CheckCircleIcon className="w-5 h-5 text-green-500" />
              ) : (
                <XCircleIcon className="w-5 h-5 text-red-500" />
              )}
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <p className="text-sm font-medium text-gray-800">
                  {user.is_active ? "Active" : "Inactive"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Created At</p>
                <p className="text-sm font-medium text-gray-800">
                  {new Date(user.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Last Login</p>
                <p className="text-sm font-medium text-gray-800">
                  {user.last_login_details
                    ? new Date(
                      user.last_login_details + "Z"
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                    : "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Date Range Filter */}
      <div className="flex items-center justify-end gap-3">
        <label className="text-sm font-medium text-gray-700">Date Range:</label>
        <CustomDropdown
          value={dateRange}
          onChange={setDateRange}
          options={[
            { value: "7", label: "Last 7 days" },
            { value: "15", label: "Last 15 days" },
            { value: "30", label: "Last 30 days" },
          ]}
        />
      </div>

      {/* Platform Insights */}
      <Accordion title="Platform Insights">
        {analyticsLoading ? (
          <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
            Loading analytics…
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="w-full rounded-xl border bg-white p-6 shadow-sm">
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Model Activity By Output</h2>
              </div>

              {totalModelUsage > 0 ? (
                <>
                  <div className="overflow-x-auto overflow-y-auto max-h-[300px] border border-slate-100 rounded-lg">
                    <table className="w-full border-collapse text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b bg-slate-50 text-slate-600">
                          <th className="px-3 py-2 text-left font-medium">
                            Model
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            Usage
                          </th>
                          <th className="px-3 py-2 text-right font-medium">
                            %
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {modelUsagePieData.map((item) => {
                          const percentage = totalModelUsage
                            ? Math.round((item.value / totalModelUsage) * 100)
                            : 0;

                          return (
                            <tr
                              key={item.name}
                              className="border-b last:border-b-0 hover:bg-slate-50 transition-colors"
                            >
                              <td className="px-3 py-2 flex items-center gap-2">
                                <span className="text-slate-700">
                                  {item.name}
                                </span>
                              </td>

                              <td className="px-3 py-2 text-right font-medium text-slate-900">
                                {item.value}
                              </td>

                              <td className="px-3 py-2 text-right text-slate-600">
                                {percentage}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-sm font-medium text-slate-700 text-right">
                    Total Model Usage:{" "}
                    <span className="font-semibold text-slate-900">
                      {totalModelUsage}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-500">
                  No model usage data available
                </div>
              )}
            </div>

            <AnalyticsChartCard
              title="Search Usage Over Time"
              subtitle=""
              {...searchUsageChart}
            />
          </div>
        )}
      </Accordion>

      {/* Projects / Collections Insights */}
      <Accordion title="Projects / Collections Insights">
        {analyticsLoading ? (
          <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
            Loading analytics…
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
            <AnalyticsChartCard
              title="Projects and Collections Over Time"
              subtitle=""
              {...projectChart}
            />

            <AnalyticsChartCard
              title="Files Uploaded in Projects and Collections"
              subtitle=""
              {...projectFileChart}
            />

            <AnalyticsChartCard
              title="Chats Created in Projects and with Collections"
              subtitle=""
              {...projectChatChart}
            />
          </div>
        )}
      </Accordion>

      {/* File Generation Insights */}
      <Accordion title="File Generation Analytics">
        {analyticsLoading ? (
          <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
            Loading analytics…
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
            <AnalyticsChartCard
              title="Image Generation Over Time"
              subtitle=""
              {...imageGenChart}
            />

            <AnalyticsChartCard
              title="File Generation Over Time"
              subtitle=""
              {...fileGenChart}
            />
          </div>
        )}
      </Accordion>

      {/* File Uploading Insights */}
      <Accordion title="File Uploading Insights">
        {analyticsLoading ? (
          <div className="flex h-[300px] items-center justify-center text-sm text-slate-400">
            Loading analytics…
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
            <AnalyticsChartCard
              title="Files uploaded over time"
              subtitle=""
              {...fileChart}
            />

            <div className="w-full rounded-xl border bg-white p-6 shadow-sm">
              <div className="mb-4 text-sm font-medium text-slate-700">
                Files by type
              </div>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={filesByTypeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    minPointSize={3}
                    fill={COLORS.secondary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-xl border bg-white p-6 shadow-sm">
              <div className="mb-4 text-sm font-medium text-slate-700">
                Files by size
              </div>

              {sizeBucketTotal > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 text-slate-600">
                        <th className="px-3 py-2 text-left font-medium">
                          Size Range
                        </th>
                        <th className="px-3 py-2 text-right font-medium">
                          Files
                        </th>
                        <th className="px-3 py-2 text-right font-medium">%</th>
                      </tr>
                    </thead>

                    <tbody>
                      {sizeBucketPieData.map((item) => {
                        const percentage = sizeBucketTotal
                          ? Math.round((item.value / sizeBucketTotal) * 100)
                          : 0;

                        return (
                          <tr
                            key={item.name}
                            className="border-b last:border-b-0"
                          >
                            <td className="px-3 py-2 flex items-center gap-2">
                              <span className="text-slate-700">{item.name}</span>
                            </td>

                            <td className="px-3 py-2 text-right font-medium text-slate-900">
                              {item.value}
                            </td>

                            <td className="px-3 py-2 text-right text-slate-600">
                              {percentage}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="mt-4 text-sm font-medium text-slate-700 text-right">
                    Total Files:{" "}
                    <span className="font-semibold text-slate-900">
                      {sizeBucketTotal}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-500">
                  No activity in this time range
                </div>
              )}
            </div>
          </div>
        )}
      </Accordion>
    </div>
  );
};

export default UserDetail;
